import { useMemo } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  fetchLookupValues,
  fetchAllLookups,
  createLookupValue,
  updateLookupValue,
  removeLookupValue,
  reorderLookupValues,
  scanLookupUsage,
  adoptLookupValues
} from 'src/util/lookups/lookupsApi';

// READING AND WRITING DROP-DOWN VALUES (TGCLOUD-1784).
//
// One shared query key per (scope, list), so a value added from a clinical
// record appears in the Settings table and in every other open picker for the
// same list, rather than only in the component that happened to make the call.
// That is the whole reason this is a query rather than a `useState` and a
// fetch: the inline Add row and the Settings screen edit the same thing.
//
// THE OPTIONS A PICKER OFFERS ARE NOT SIMPLY THE ENABLED ROWS. They are the
// enabled rows PLUS the value the record already holds, whenever that value is
// not among them. Without that, a record whose value was disabled, imported,
// written by Calendly or created from a form title could not be re-saved: the
// shared autocomplete silently drops any chosen label that is not in its
// options, and for a genetics user it is not free-text either, so the field
// would clear itself on save with no error. `useLookupOptions` is where that
// rule lives so no individual form has to remember it.

export const lookupsKey = (scope) => ['lookups', scope || 'own'];
export const lookupListKey = (listKey, scope) => [
  'lookups',
  scope || 'own',
  listKey
];

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * One list's values, as the picker should offer them.
 *
 * @param {string} listKey
 * @param {{ scope?: string, includeDisabled?: boolean, enabled?: boolean }} [options]
 *   `scope` is a target user group id for an admin editing another group, or
 *   the string 'system' for the deployment's standard set.
 */
export function useLookupList(listKey, options = {}) {
  const { scope, includeDisabled = false, enabled = true } = options;

  const { data, isLoading, isError } = useQuery({
    queryKey: [...lookupListKey(listKey, scope), { includeDisabled }],
    staleTime: FIVE_MINUTES,
    // A site that has never configured a list is the common case and an empty
    // list is correct for it, so a failed fetch is not worth retrying: the
    // picker falls back to offering only the record's own value, which still
    // lets the record be saved.
    retry: false,
    enabled: Boolean(listKey) && enabled,
    queryFn: () =>
      fetchLookupValues(listKey, {
        includeDisabled,
        targetUserGroup: scope && scope !== 'system' ? scope : undefined,
        scope: scope === 'system' ? 'system' : undefined
      })
  });

  return {
    values: data || [],
    isLoading,
    isError
  };
}

/**
 * Teach the status registry what THIS group's status rows mean, for a screen
 * that branches on a status without ever opening a form (TGCLOUD-1751): the
 * home Overdue panel, the pathway board, the pedigree overlays.
 *
 * Before this, only the appointment form and Settings fetched the status
 * lists, so those screens understood a site's own wording only if a form had
 * happened to load first in the same session, and never understood a row the
 * site had switched off. This reads every row, disabled and withdrawn ones
 * included, because a record holding a switched-off value still has to read
 * as what it means. The picker's own enabled-only read is unaffected.
 *
 * Returns a version string that changes whenever any of the lists arrives, so
 * a caller that memoises a status-dependent computation can depend on it.
 *
 * @param {string[]} listKeys coded lists, e.g. ['appointmentStatuses']
 * @param {{ enabled?: boolean }} [options]
 * @returns {string}
 */
export function useStatusMeanings(listKeys, options = {}) {
  const { enabled = true } = options;
  const results = useQueries({
    queries: (listKeys || []).map((listKey) => ({
      queryKey: [...lookupListKey(listKey), { includeDisabled: true }],
      staleTime: FIVE_MINUTES,
      // A failure leaves the shipped wording in place, which is the safe
      // reading; it is not worth retrying on a home screen.
      retry: false,
      enabled: Boolean(listKey) && enabled,
      queryFn: () => fetchLookupValues(listKey, { includeDisabled: true })
    }))
  });
  return results.map((r) => r.dataUpdatedAt || 0).join('|');
}

/**
 * Every list, for the Settings screen.
 */
export function useAllLookups(options = {}) {
  const { scope, includeDisabled = true, enabled = true } = options;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...lookupsKey(scope), { includeDisabled }],
    staleTime: FIVE_MINUTES,
    retry: false,
    // A System Admin who has not chosen a group has nothing to fetch FOR:
    // their own group is a sentinel with no group document, so the request
    // would return rows nobody can read. The caller passes `enabled: false`
    // rather than this hook guessing.
    enabled,
    queryFn: () =>
      fetchAllLookups({
        includeDisabled,
        targetUserGroup: scope && scope !== 'system' ? scope : undefined,
        scope: scope === 'system' ? 'system' : undefined
      })
  });

  return {
    lists: data?.lists || [],
    groups: data?.groups || [],
    isLoading,
    isError,
    refetch
  };
}

/**
 * Merge a record's CURRENT value into the offered options.
 *
 * Exported as a pure function so the rule can be tested without a query
 * client, which is the house pattern here, and so a form that already has its
 * rows can apply it without a second fetch.
 *
 * @param {Array<{label: string, meaning: string|null}>} values enabled rows
 * @param {string} currentValue what the record holds, if anything
 * @param {{ storesMeaning?: boolean }} [options]
 * @returns {Array<{label: string, value: string, unlisted?: boolean}>}
 */
export const withCurrentValue = (values, currentValue, options = {}) => {
  const rows = (values || []).map((row) => ({
    ...row,
    // `value` is what lands on the record. For the three lists whose records
    // hold a code rather than a label, that is the meaning.
    value: options.storesMeaning ? row.meaning : row.label
  }));

  const current = String(currentValue ?? '').trim();
  if (!current) return rows;

  const held = rows.some(
    (row) =>
      String(row.value ?? '')
        .trim()
        .toLowerCase() === current.toLowerCase() ||
      String(row.label ?? '')
        .trim()
        .toLowerCase() === current.toLowerCase()
  );
  if (held) return rows;

  // Flagged rather than silently offered, so a clinician can see that this
  // value is no longer one of the clinic's choices while still being able to
  // save the record in front of them.
  return [
    ...rows,
    {
      _id: `unlisted:${current}`,
      label: `${current} (no longer offered)`,
      value: current,
      unlisted: true
    }
  ];
};

/**
 * The options one picker should render, current value included.
 */
export function useLookupOptions(listKey, currentValue, options = {}) {
  const { values, isLoading, isError } = useLookupList(listKey, options);
  const optionsForPicker = useMemo(
    () => withCurrentValue(values, currentValue, options),
    // `options.storesMeaning` is the only part of `options` this reads, and
    // spreading the object into the dependency list would rebuild on every
    // render because callers pass an inline literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, currentValue, options.storesMeaning]
  );
  return { options: optionsForPicker, isLoading, isError };
}

/**
 * Add, change, remove and reorder, each refreshing every consumer of the list.
 *
 * @param {string} listKey
 * @param {{ scope?: string, owner?: string }} [options] `owner` is the record
 *   root's id, sent from a clinical record so a System Admin's inline add
 *   lands on the group that owns the family.
 */
export function useLookupMutations(listKey, options = {}) {
  const { scope, owner } = options;
  const queryClient = useQueryClient();

  return useMemo(() => {
    const scoped = (body = {}) => {
      const payload = { ...body };
      if (scope && scope !== 'system') payload.targetUserGroup = scope;
      if (owner) payload.owner = owner;
      return payload;
    };

    // Invalidate the LIST, not the whole cache. React Query v5 takes a filters
    // object; passing a bare array here matches every query in the app, which
    // is a mistake already present elsewhere in this repo.
    const refresh = () =>
      queryClient.invalidateQueries({
        queryKey: lookupListKey(listKey, scope)
      });

    const refreshAll = () =>
      queryClient.invalidateQueries({ queryKey: lookupsKey(scope) });

    return {
      add: async (label, extra = {}) => {
        const result = await createLookupValue(
          listKey,
          scoped({ label, ...extra })
        );
        await refresh();
        await refreshAll();
        return result;
      },
      patch: async (id, patch) => {
        const result = await updateLookupValue(listKey, id, scoped(patch));
        await refresh();
        await refreshAll();
        return result;
      },
      disable: async (id) => {
        const result = await updateLookupValue(
          listKey,
          id,
          scoped({ enabled: false })
        );
        await refresh();
        await refreshAll();
        return result;
      },
      remove: async (id) => {
        const result = await removeLookupValue(
          listKey,
          id,
          scope && scope !== 'system' ? { targetUserGroup: scope } : {}
        );
        await refresh();
        await refreshAll();
        return result;
      },
      reorder: async (orderedIds) => {
        const result = await reorderLookupValues(listKey, orderedIds, scoped());
        await refresh();
        await refreshAll();
        return result;
      },
      // A scan writes a count onto every row, so the table has to be refetched
      // afterwards even though nobody edited a value.
      scan: async () => {
        const result = await scanLookupUsage(listKey, {
          targetUserGroup: scope && scope !== 'system' ? scope : undefined
        });
        await refresh();
        await refreshAll();
        return result;
      },
      adopt: async (labels) => {
        const result = await adoptLookupValues(listKey, labels, scoped());
        await refresh();
        await refreshAll();
        return result;
      }
    };
  }, [listKey, scope, owner, queryClient]);
}
